import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import { Collapsible } from 'tio-ui/components/utilities';
import { getSingleValueForTasField } from 'tio-common/utils/tuition-assistance/fields';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t, type IntlService } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import ApplicationRow from 'tio-employee/components/tas/programs/application-row';
import Component from '@glimmer/component';
import MyPaymentActivity from 'tio-employee/components/tuition-assistance/dashboard/my-payment-activity';
import pageTitle from 'ember-page-title/helpers/page-title';
import ProgramDescription from 'tio-employee/components/tas/programs/program-description';
import TioAlert from 'tio-common/components/tio/alert';
import transitionTo from 'ember-router-helpers/helpers/transition-to';
import { courseCashDashboard } from 'tio-common/utils/tours/course-cash-tour';
import type TourManager from 'tio-common/services/tour-manager';
import type { LimitsStatusDataSignature } from 'tio-common/services/tuition-assistance';
import type SessionContextService from 'tio-employee/services/session-context';
import type TasProgramInstanceModel from 'tio-common/models/tas-program-instance';
import type TasProgramTemplate from 'tio-common/models/tas-program-template';
import SortPopover from 'tio-common/components/tas/sort-popover';
import { PhCaretRight } from 'ember-phosphor-icons';
import TourTrigger from 'tio-common/components/tours/tour-trigger';
import { htmlSafe } from '@ember/template';

export interface S {
  Args: {
    model: {
      isHeldParticipant: boolean;
      activeInstances: TasProgramInstanceModel[];
      templates: TasProgramTemplate[];
      pastInstances: TasProgramInstanceModel[];
      limitsStatus: LimitsStatusDataSignature;
    };
  };
}

export default class TasDashboard extends Component<S> {
  @service declare intl: IntlService;
  @service declare sessionContext: SessionContextService;
  @service declare tourManager: TourManager;
  @tracked isOpen = false;
  @tracked currentSort = 'alphabetical';
  @tracked sortOrderAscending = true;

  constructor(owner: unknown, args: S['Args']) {
    // @ts-expect-error - Owner type mismatch is ok here
    super(owner, args);
    this.registerTours();
  }

  get hasAcceptedTerms() {
    return !this.sessionContext.needsToAcceptAppTerms;
  }

  private registerTours() {
    if (this.hasCourseCashPrograms && this.companySettings?.tuitionAssistance?.isDirectPayEnabled) {
      this.tourManager.registerTour(courseCashDashboard);
    }
  }

  get isDependentProgramsEnabled() {
    return (
      this.sessionContext.currentEmployee?.company.companySetting.tuitionAssistance
        ?.isDependentProgramsEnabled || false
    );
  }

  getExpenseType(template: TasProgramTemplate) {
    const expenseType = getSingleValueForTasField('EXPENSE_TYPE', template.fields);
    if (expenseType) {
      return `${expenseType}s`;
    } else {
      return 'COURSES';
    }
  }

  toggle = () => {
    this.isOpen = !this.isOpen;
  };

  updateSort = (sort: string) => {
    this.currentSort = sort;
  };

  updateSortOrder = () => {
    this.sortOrderAscending = !this.sortOrderAscending;
  };

  get sortedTemplates() {
    const list = [...this.args.model.templates];

    const sorted = list.sort((a, b) => {
      if (this.currentSort === 'alphabetical') {
        return a.programName.localeCompare(b.programName);
      } else {
        return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      }
    });

    return this.sortOrderAscending ? sorted : sorted.reverse();
  }

  get hasCourseCashPrograms() {
    return this.args.model.templates.some((template) => template.isDirectPayProgram);
  }

  get companySettings() {
    return this.sessionContext.currentEmployee?.company.companySetting;
  }

  <template>
    {{pageTitle (t "dashboard")}}
    {{#if this.hasAcceptedTerms}}
      <TourTrigger @tourId="course-cash-dashboard" @autoStart={{true}} @delay={{500}} />
    {{/if}}
    <VStack>
      <Header>{{t "tuition_assistance.default"}}</Header>
      {{#if @model.isHeldParticipant}}
        <TioAlert @type="warning" class="my-4" @allowDismiss={{true}}>
          <:header>
            <p class="font-bold">
              {{t "tuition_assistance.held_participant_warnings.dashboard_title"}}
            </p>
          </:header>
          <:body>
            <p class="text-sm">
              {{t "tuition_assistance.held_participant_warnings.dashboard_description"}}
            </p>
          </:body>
        </TioAlert>
      {{/if}}
      {{#unless this.companySettings.tuitionAssistance.hideAnnualBenefitsCard}}
        <Section>
          <:header>
            <div class="flex justify-between items-center">
              <p>{{t "tuition_assistance.dashboard.payment_activity.payments"}}</p>
            </div>
          </:header>
          <:body>
            <h3 class="font-semibold mt-4">
              {{t "tuition_assistance.dashboard.payment_activity.total_payments"}}
            </h3>
            <p>
              {{t "tuition_assistance.dashboard.payment_activity.note"}}
            </p>
            <MyPaymentActivity class="m-2 mb-16" @limitsStatus={{@model.limitsStatus}} />
          </:body>
        </Section>
      {{/unless}}
      <Section>
        <:header>{{t "common.active"}}</:header>
        <:actions>
          {{#if this.isDependentProgramsEnabled}}
            <Button
              @intent="primary"
              @appearance="outlined"
              {{on "click" (transitionTo "authenticated.tas.dependent-programs")}}
              data-test-dependent-programs-button
              id="course-cash-overview"
            >
              {{t "common.tuition_assistance.dependent_programs_button"}}
            </Button>
          {{/if}}
        </:actions>
        <:body>
          <VStack @collapsed={{true}}>
            {{#each @model.activeInstances as |instance|}}
              <Section>
                <ProgramDescription @instance={{instance}} />
                {{#if instance.tasApplications.length}}
                  <ApplicationRow
                    @applications={{instance.tasApplications}}
                    @expenseType={{this.getExpenseType instance.tasProgramTemplate}}
                  />
                {{/if}}
              </Section>
            {{/each}}
          </VStack>
        </:body>
      </Section>
      <Section>
        <:header>{{t "tas.dashboard.available_benefits"}}</:header>
        <:subHeader>
          {{t "tas.dashboard.available_benefits_subheader" number=this.sortedTemplates.length}}
        </:subHeader>
        <:actions>
          <SortPopover
            @initial={{this.currentSort}}
            @sortOrder={{this.sortOrderAscending}}
            @onSortSelect={{this.updateSort}}
            @updateSortOrder={{this.updateSortOrder}}
          />
        </:actions>
        <:body>
          <VStack @collapsed={{true}}>
            {{#each this.sortedTemplates as |template|}}
              <Section>
                <div class="flex">
                  <LinkTo
                    @route="authenticated.tas.getting-started"
                    @model={{template.id}}
                    class="font-semibold text-lg text-ocean-600 hover:underline"
                  >
                    {{template.programName}}
                  </LinkTo>
                  {{#if template.isDirectPayProgram}}
                    <Chip class="ml-2" id="course-cash-eligible" @intent="primary">{{htmlSafe
                        (t "common.tas.course_cash.default")
                      }}</Chip>
                  {{/if}}
                </div>
                {{#if template.programDescription}}
                  <p class="flex justify-between">
                    {{template.programDescription}}
                  </p>
                {{/if}}
              </Section>
            {{/each}}
          </VStack>
        </:body>
      </Section>
      {{! TODO: figure out the logic on when we show this. We want to show things that only closed out recently
      in the main list. Thinking like 30 days. Also figma shows this as a history page on it's own, but that placement is
      currently occupied by the dependent button so we need a home for that first? }}
      {{#if @model.pastInstances.length}}
        <VStack class="mb-8!">
          <Section>
            <:body>
              <button
                type="button"
                class="font-semibold text-lg flex justify-between w-full"
                {{on "click" this.toggle}}
                id="course-cash-apply-button"
              >
                {{t "tas.history.past_programs"}}
                <PhCaretRight @size="24" class="text-purple-700 {{if this.isOpen 'rotate-90'}}" />
              </button>
              <Collapsible @isOpen={{this.isOpen}}>
                <VStack @collapsed={{true}}>
                  {{#each @model.pastInstances as |instance|}}
                    <Section>
                      <ProgramDescription @instance={{instance}} />
                      {{#if instance.tasApplications.length}}
                        <ApplicationRow
                          @applications={{instance.tasApplications}}
                          @expenseType={{this.getExpenseType instance.tasProgramTemplate}}
                        />
                      {{/if}}
                    </Section>
                  {{/each}}
                </VStack>
              </Collapsible>
            </:body>
          </Section>
        </VStack>
      {{/if}}
    </VStack>
  </template>
}
